import React from 'react';
import { Box, Typography, Card, CardContent, Chip } from '@mui/material';
import { TrendingUp, Psychology, SwapHoriz, Explore, Timeline, SportsScore } from '@mui/icons-material';

interface ProfileCardProps {
  profile: any;
  showDualCareer?: boolean;
}

const ProfileCard: React.FC<ProfileCardProps> = ({ profile, showDualCareer = true }) => {
  if (!profile) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* サマリー */}
      <Card>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            プロフィールサマリー
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.8, color: '#333' }}>
            {profile.summary}
          </Typography>
        </CardContent>
      </Card>

      {/* 強み */}
      <Card>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <TrendingUp sx={{ color: '#4caf50', mr: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>強み</Typography>
          </Box>
          {profile.strengths?.map((s: any, i: number) => (
            <Box key={i} sx={{ mb: 2, p: 2, bgcolor: '#f0fff4', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{s.name}</Typography>
                <Chip label={`${s.score}%`} size="small" color="success" />
              </Box>
              <Typography variant="body2" sx={{ color: '#666' }}>{s.description}</Typography>
            </Box>
          ))}
        </CardContent>
      </Card>

      {/* 行動特性 */}
      <Card>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Psychology sx={{ color: '#2196f3', mr: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>行動特性</Typography>
          </Box>
          {profile.behavioralTraits?.hardExperience && (
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" sx={{ color: '#999' }}>最も苦しかった経験</Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>{profile.behavioralTraits.hardExperience}</Typography>
            </Box>
          )}
          {profile.behavioralTraits?.overcomingMethod && (
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" sx={{ color: '#999' }}>乗り越えた方法</Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>{profile.behavioralTraits.overcomingMethod}</Typography>
            </Box>
          )}
          {profile.behavioralTraits?.teamRole && (
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" sx={{ color: '#999' }}>チーム内の役割</Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>{profile.behavioralTraits.teamRole}</Typography>
            </Box>
          )}
          <Box sx={{ mt: 2, p: 2, bgcolor: '#f0f4ff', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ lineHeight: 1.8 }}>
              {profile.behavioralTraits?.analysis}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* 経験→能力変換 */}
      <Card>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <SwapHoriz sx={{ color: '#ff9800', mr: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>経験から得た能力</Typography>
          </Box>
          {profile.experienceConversion?.map((conv: any, i: number) => (
            <Box key={i} sx={{ mb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {conv.experience}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                {conv.abilities?.map((a: string, j: number) => (
                  <Chip key={j} label={a} size="small" variant="outlined" color="warning" />
                ))}
              </Box>
            </Box>
          ))}
        </CardContent>
      </Card>

      {/* キャリア志向 */}
      <Card>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Explore sx={{ color: '#9c27b0', mr: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>キャリア志向</Typography>
          </Box>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
            {profile.careerOrientation?.summary}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {profile.aptitudeAreas?.map((area: string, i: number) => (
              <Chip key={i} label={area} color="primary" />
            ))}
          </Box>
        </CardContent>
      </Card>

      {/* 成長ポテンシャル */}
      <Card>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Timeline sx={{ color: '#00bcd4', mr: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>成長ポテンシャル</Typography>
          </Box>
          {profile.growthPotential?.map((g: any, i: number) => (
            <Box key={i} sx={{ mb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{g.area}</Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>{g.description}</Typography>
            </Box>
          ))}
        </CardContent>
      </Card>

      {/* デュアルキャリア */}
      {showDualCareer && profile.dualCareer && (
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <SportsScore sx={{ color: '#f44336', mr: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>デュアルキャリア条件</Typography>
            </Box>
            {profile.dualCareer?.conditions && (
              <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                {profile.dualCareer.conditions.practice && (
                  <Box><Typography variant="caption" sx={{ color: '#999' }}>練習</Typography><Typography variant="body2">{profile.dualCareer.conditions.practice}</Typography></Box>
                )}
                {profile.dualCareer.conditions.competition && (
                  <Box><Typography variant="caption" sx={{ color: '#999' }}>大会</Typography><Typography variant="body2">{profile.dualCareer.conditions.competition}</Typography></Box>
                )}
                {profile.dualCareer.conditions.competitionPriority && (
                  <Box><Typography variant="caption" sx={{ color: '#999' }}>優先度</Typography><Typography variant="body2">{profile.dualCareer.conditions.competitionPriority}</Typography></Box>
                )}
                {profile.dualCareer.conditions.workStyle && (
                  <Box><Typography variant="caption" sx={{ color: '#999' }}>働き方</Typography><Typography variant="body2">{profile.dualCareer.conditions.workStyle}</Typography></Box>
                )}
                {profile.dualCareer.conditions.nonNegotiable && (
                  <Box><Typography variant="caption" sx={{ color: '#999' }}>譲れない条件</Typography><Typography variant="body2">{profile.dualCareer.conditions.nonNegotiable}</Typography></Box>
                )}
              </Box>
            )}
            <Box sx={{ p: 2, bgcolor: '#fef0f0', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ lineHeight: 1.8 }}>
                {profile.dualCareer?.summary}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* キーワード */}
      {profile.keywords?.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
          {profile.keywords.map((kw: string, i: number) => (
            kw && <Chip key={i} label={`#${kw}`} variant="outlined" sx={{ fontSize: '0.95rem' }} />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default ProfileCard;
